import { useState } from 'react';

import { AGGREGATE_NAMES, CHAT_COMMANDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { LuPlus } from 'react-icons/lu';
import { toast } from 'react-toastify';

import { LinkCaseSearch } from './LinkCaseSearch';
import { LinkedCaseCard } from './LinkedCaseCard';
import { PanelSection } from './PanelSection';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { useLinkChatMutation } from '~/features/whatsapp/hooks/useLinkChatMutation';
import { useUnlinkChatMutation } from '~/features/whatsapp/hooks/useUnlinkChatMutation';

type Props = {
    chatId: string;
    loanRequestIds: Array<string>;
    partnerIds: Array<string>;
    phone: string | null;
    phoneNumberId: string;
};

export const LinkedCasesSection = ({ chatId, loanRequestIds, partnerIds, phone, phoneNumberId }: Props) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(loanRequestIds.length === 0);
    const { permissions } = useAclPermissions({
        actionFilter: {
            [AGGREGATE_NAMES.CHAT]: [CHAT_COMMANDS.linkChat, CHAT_COMMANDS.unlinkChat],
        },
        chatLinkInfo: { chatId, loanRequestIds, partnerIds, ...(phone && { phone }), phoneNumberId },
    });
    const linkMutation = useLinkChatMutation(chatId);
    const unlinkMutation = useUnlinkChatMutation(chatId);

    const canLink = permissions.commands[AGGREGATE_NAMES.CHAT][CHAT_COMMANDS.linkChat];
    const canUnlink = permissions.commands[AGGREGATE_NAMES.CHAT][CHAT_COMMANDS.unlinkChat];

    return (
        <PanelSection
            open={isOpen}
            onOpenChange={setIsOpen}
            title={t('WHATSAPP_CHAT.USER_INFO.LINKED_CASES.TITLE', { count: loanRequestIds.length })}
        >
            <div className="flex flex-col gap-2 py-2">
                {loanRequestIds.map((loanRequestId) => (
                    <LinkedCaseCard
                        key={loanRequestId}
                        loanRequestId={loanRequestId}
                        canUnlink={canUnlink}
                        isUnlinking={unlinkMutation.isPending}
                        onUnlink={() => unlinkMutation.mutate(loanRequestId)}
                    />
                ))}
                {loanRequestIds.length === 0 && (
                    <p className="text-sm text-grey">{t('WHATSAPP_CHAT.USER_INFO.LINKED_CASES.EMPTY')}</p>
                )}
                {canLink && (
                    <>
                        <div className="mt-1 flex items-center gap-1 text-sm font-medium text-midnight">
                            <LuPlus size={12} />
                            <span>{t('WHATSAPP_CHAT.USER_INFO.LINKED_CASES.SEARCH_HINT')}</span>
                        </div>
                        <LinkCaseSearch
                            excludeLoanRequestIds={loanRequestIds}
                            isLinking={linkMutation.isPending}
                            onSelect={(loanRequestId) =>
                                linkMutation.mutate(loanRequestId, {
                                    onSuccess: () =>
                                        toast.success(t('WHATSAPP_CHAT.USER_INFO.LINKED_CASES.LINK_SUCCESS')),
                                })
                            }
                        />
                    </>
                )}
            </div>
        </PanelSection>
    );
};
